import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home, SearchX } from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';

/**
 * Route-level error element used by react-router's errorElement.
 * Handles both 404s (from bad routes) and runtime errors thrown
 * inside route loaders, actions, or components.
 */
export default function RouteErrorPage() {
    const error = useRouteError();
    const isNotFound = isRouteErrorResponse(error) && error.status === 404;

    if (isNotFound) {
        return (
            <div className="min-h-screen flex flex-col bg-background text-foreground">
                <Navbar />
                <main className="flex-1 flex items-center justify-center p-6">
                    <div className="max-w-lg w-full text-center">
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 200, damping: 20 }}
                            className="relative mx-auto mb-10"
                        >
                            <div className="relative inline-flex items-center justify-center">
                                <div className="absolute inset-0 bg-primary/5 rounded-full blur-3xl scale-150" />
                                <div className="relative flex items-center gap-2">
                                    <span className="text-8xl sm:text-9xl font-black tracking-tighter text-primary/20 select-none">4</span>
                                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-primary/15 to-primary/5 rounded-3xl flex items-center justify-center border border-primary/10 shadow-xl shadow-primary/5">
                                        <SearchX className="h-10 w-10 sm:h-12 sm:w-12 text-primary/60" strokeWidth={1.5} />
                                    </div>
                                    <span className="text-8xl sm:text-9xl font-black tracking-tighter text-primary/20 select-none">4</span>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3 }}
                        >
                            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Page not found</h1>
                            <p className="text-muted-foreground leading-relaxed max-w-sm mx-auto mb-10">
                                Looks like this page doesn't exist or has been moved. Let's get you back on track.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Button asChild className="rounded-full h-12 px-8 gap-2 font-semibold shadow-lg shadow-primary/20">
                                    <Link to="/"><Home className="h-4 w-4" /> Back to Home</Link>
                                </Button>
                                <Button variant="outline" className="rounded-full h-12 px-8 gap-2 font-semibold" onClick={() => window.history.back()}>
                                    Go Back
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                </main>
            </div>
        );
    }

    // Runtime / unexpected error
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

    return (
        <div className="min-h-screen flex flex-col bg-background text-foreground">
            <Navbar />
            <main className="flex-1 flex items-center justify-center p-6">
                <div className="max-w-md w-full text-center">
                    <div className="relative mx-auto mb-8 w-28 h-28">
                        <div className="absolute inset-0 bg-destructive/10 rounded-full animate-ping opacity-30" />
                        <div className="relative w-28 h-28 bg-gradient-to-br from-destructive/20 to-destructive/5 rounded-full flex items-center justify-center border border-destructive/20">
                            <AlertTriangle className="h-12 w-12 text-destructive" strokeWidth={1.5} />
                        </div>
                    </div>

                    <h1 className="text-3xl font-bold tracking-tight mb-3">Something went wrong</h1>
                    <p className="text-muted-foreground leading-relaxed mb-2">
                        An unexpected error occurred. Don't worry, your data is safe.
                    </p>

                    <div className="mb-8 mt-4 p-4 bg-muted/50 rounded-2xl border border-border/50 text-left">
                        <p className="text-xs font-mono text-muted-foreground break-all leading-relaxed">
                            {errorMessage}
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                            onClick={() => window.location.reload()}
                            className="rounded-full h-12 px-8 gap-2 font-semibold shadow-lg shadow-primary/20"
                        >
                            <RefreshCw className="h-4 w-4" />
                            Try Again
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => { window.location.href = '/'; }}
                            className="rounded-full h-12 px-8 gap-2 font-semibold"
                        >
                            <Home className="h-4 w-4" />
                            Go Home
                        </Button>
                    </div>
                </div>
            </main>
        </div>
    );
}
